import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Calendar, CreditCard, User, MapPin, Truck, ChevronRight, X, Package } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import PageLoadingState from '../components/PageLoadingState';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ImageModal({ isOpen, onClose, imageUrl, productName }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] md:max-w-[70vw] lg:max-w-[50vw] p-0 overflow-hidden bg-black/90 border-white/10">
        <DialogTitle className="sr-only">
          Enlarged view of {productName}
        </DialogTitle>
        <div className="relative w-full h-full flex items-center justify-center min-h-[50vh]">
          <img
            src={imageUrl}
            alt={productName}
            className="max-w-full max-h-[85vh] object-contain"
          />
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-white text-center font-medium">{productName}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trackingInfo, setTrackingInfo] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: "",
    productName: "",
  });

  const fetchOrders = useCallback(async () => {
    const token = sessionStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/admin/orders`, {
        params: { status: statusFilter },
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
      
      // Initialize tracking info state
      const initialTracking = {};
      response.data.forEach(order => {
        initialTracking[order.id] = {
          tracking_number: order.tracking_number || '',
          courier_service: order.courier_service || '',
          delivery_timeframe: order.delivery_timeframe || ''
        };
      });
      setTrackingInfo(initialTracking);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleStatusUpdate = async (orderId, newStatus) => {
    const token = sessionStorage.getItem('token');
    const updateData = { order_status: newStatus };
    
    // Include delivery timeframe if status is accepted or confirmed
    if ((newStatus === 'accepted' || newStatus === 'confirmed') && trackingInfo[orderId]) {
      if (trackingInfo[orderId].delivery_timeframe) {
        updateData.delivery_timeframe = trackingInfo[orderId].delivery_timeframe;
      }
    }
    
    // Include tracking info if status is shipped or delivered
    if ((newStatus === 'shipped' || newStatus === 'delivered') && trackingInfo[orderId]) {
      if (trackingInfo[orderId].tracking_number) {
        updateData.tracking_number = trackingInfo[orderId].tracking_number;
      }
      if (trackingInfo[orderId].courier_service) {
        updateData.courier_service = trackingInfo[orderId].courier_service;
      }
    }
    
    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/status`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order status updated successfully');
      fetchOrders();
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({ ...selectedOrder, order_status: newStatus, ...updateData });
      }
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const updateTrackingInfo = (orderId, field, value) => {
    setTrackingInfo(prev => ({
      ...prev,
      [orderId]: {
        ...prev[orderId],
        [field]: value
      }
    }));
  };

  const saveTrackingInfo = async (orderId) => {
    const token = sessionStorage.getItem('token');
    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/status`,
        {
          order_status: orders.find(o => o.id === orderId)?.order_status,
          ...trackingInfo[orderId]
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Tracking information updated');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update tracking information');
    }
  };

  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setIsDetailsOpen(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      placed: 'bg-blue-500/10 text-blue-500',
      accepted: 'bg-purple-500/10 text-purple-500',
      confirmed: 'bg-green-500/10 text-green-500',
      packed: 'bg-yellow-500/10 text-yellow-500',
      shipped: 'bg-indigo-500/10 text-indigo-500',
      delivered: 'bg-accent/10 text-accent',
      cancelled: 'bg-destructive/10 text-destructive'
    };
    return colors[status] || 'bg-primary/10 text-primary';
  };

  if (loading && orders.length === 0) {
    return (
      <PageLoadingState title="Loading admin orders" />
    );
  }

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 pb-20" data-testid="admin-orders-page">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
            <div>
              <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-2" data-testid="orders-title">Orders</h1>
              <p className="text-muted-foreground">Manage customer orders and shipments</p>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex flex-col gap-1.5 min-w-[200px]">
                <Label htmlFor="status-filter" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Filter by Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger id="status-filter" className="glass-morphic border-white/10 h-11">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="placed">Placed</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="packed">Packed</SelectItem>
                    <SelectItem value="shipped">Shipped</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {orders.length > 0 ? (
            <div className="grid gap-6">
              {orders.map((order, index) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="glass-morphic rounded-2xl overflow-hidden hover:border-primary/30 transition-all duration-300 cursor-pointer group"
                  onClick={() => openOrderDetails(order)}
                  data-testid={`order-${order.id}`}
                >
                  <div className="p-6">
                    <div className="flex flex-col lg:flex-row justify-between gap-6">
                      <div className="flex-1 space-y-4">
                        <div className="flex items-center gap-4">
                          <div className="bg-primary/10 p-2.5 rounded-xl">
                            <ShoppingBag className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Order ID</p>
                            <p className="font-mono text-sm font-medium">#{order.id.slice(0, 12).toUpperCase()}</p>
                          </div>
                          <span className={`ml-4 px-3 py-1 rounded-full font-semibold text-xs uppercase tracking-wider ${getStatusColor(order.order_status)}`}>
                            {order.order_status}
                          </span>
                          <span className={`ml-2 px-3 py-1 rounded-full font-semibold text-[10px] uppercase tracking-wider ${
                            order.payment_status === 'paid' || order.payment_status === 'Cash on Delivery' 
                              ? 'bg-green-500/10 text-green-500' 
                              : order.payment_status === 'payment failed'
                              ? 'bg-red-500/10 text-red-500'
                              : 'bg-yellow-500/10 text-yellow-500'
                          }`}>
                            {order.payment_status}
                          </span>
                        </div>

                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-2">
                          <div className="flex items-center gap-3">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <div className="text-sm">
                              <p className="text-muted-foreground text-xs">Customer</p>
                              <p className="font-medium">{order.shipping_address.fullName}</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <div className="text-sm">
                              <p className="text-muted-foreground text-xs">Placed on</p>
                              <p className="font-medium">
                                {new Date(order.created_at).toLocaleDateString('en-IN', {
                                  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                })}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <CreditCard className="h-4 w-4 text-muted-foreground" />
                            <div className="text-sm">
                              <p className="text-muted-foreground text-xs">Amount</p>
                              <p className="font-medium text-primary">₹{order.total_amount.toFixed(2)}</p>
                            </div>
                          </div>
                        </div>

                        {order.items && order.items.some(item => item && item.note) && (
                          <div className="mt-4 p-3 bg-amber-500/10 rounded-xl border border-amber-500/20">
                            <p className="text-[10px] text-amber-500 uppercase tracking-widest font-bold mb-1">⚠️ CUSTOMER NOTES PRESENT</p>
                            <div className="flex flex-col gap-1">
                              {order.items.filter(item => item && item.note).map((item, i) => (
                                <p key={i} className="text-xs text-muted-foreground italic truncate">
                                  <span className="font-semibold not-italic text-foreground">{item.product_name || 'Product'}:</span> "{item.note}"
                                </p>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-end">
                        <div className="h-10 w-10 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                          <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-32 glass-morphic rounded-3xl" data-testid="no-orders">
              <div className="bg-white/5 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <ShoppingBag className="h-10 w-10 text-muted-foreground" />
              </div>
              <p className="text-xl text-muted-foreground font-medium">No orders found</p>
              <p className="text-sm text-muted-foreground/60 mt-1">Try changing your filters</p>
            </div>
          )}
        </motion.div>

        {/* Order Details Dialog */}
        <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto glass-morphic border-white/10 p-0 sm:rounded-3xl">
            {selectedOrder && (
              <div className="relative">
                <div className="p-8 space-y-8">
                  <DialogHeader className="flex flex-col sm:flex-row justify-between items-start gap-4 border-b border-white/10 pb-8 space-y-0">
                    <div>
                      <DialogTitle className="text-3xl font-bold tracking-tight mb-2">Order Details</DialogTitle>
                      <DialogDescription className="text-muted-foreground font-mono">#{selectedOrder.id.toUpperCase()}</DialogDescription>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <p className="text-sm text-muted-foreground">Order Status</p>
                      <Select 
                        value={selectedOrder.order_status} 
                        onValueChange={(value) => handleStatusUpdate(selectedOrder.id, value)}
                      >
                        <SelectTrigger className={`w-40 h-10 font-semibold ${getStatusColor(selectedOrder.order_status)}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="placed">Placed</SelectItem>
                          <SelectItem value="accepted">Accepted</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="packed">Packed</SelectItem>
                          <SelectItem value="shipped">Shipped</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </DialogHeader>

                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div className="glass-morphic rounded-2xl p-6 border-white/5">
                        <div className="flex items-center gap-3 mb-4">
                          <User className="h-5 w-5 text-primary" />
                          <h3 className="font-semibold">Customer Information</h3>
                        </div>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-muted-foreground">Name:</span> {selectedOrder.shipping_address.fullName}</p>
                          <p><span className="text-muted-foreground">Phone:</span> {selectedOrder.shipping_address.phone}</p>
                          <p><span className="text-muted-foreground">Email:</span> {selectedOrder.user_email || 'N/A'}</p>
                        </div>
                      </div>

                      <div className="glass-morphic rounded-2xl p-6 border-white/5">
                        <div className="flex items-center gap-3 mb-4">
                          <MapPin className="h-5 w-5 text-primary" />
                          <h3 className="font-semibold">Shipping Address</h3>
                        </div>
                        <div className="text-sm space-y-1">
                          <p className="font-medium">{selectedOrder.shipping_address.fullName}</p>
                          <p>{selectedOrder.shipping_address.address}</p>
                          <p>{selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} - {selectedOrder.shipping_address.pincode}</p>
                        </div>
                      </div>

                      <div className="glass-morphic rounded-2xl p-6 border-white/5">
                        <div className="flex items-center gap-3 mb-4">
                          <CreditCard className="h-5 w-5 text-primary" />
                          <h3 className="font-semibold">Payment Details</h3>
                        </div>
                        <div className="space-y-3 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Method:</span>
                            <span className="capitalize">{selectedOrder.payment_method === 'cod' ? 'Cash on Delivery' : 'Online'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Status:</span>
                            <span className={`font-semibold ${
                              selectedOrder.payment_status === 'paid' ? 'text-accent' : 'text-yellow-500'
                            }`}>{selectedOrder.payment_status.toUpperCase()}</span>
                          </div>
                          {selectedOrder.razorpay_payment_id && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Payment ID:</span>
                              <span className="font-mono text-xs">{selectedOrder.razorpay_payment_id}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                          <div className="glass-morphic rounded-2xl p-6 border-white/5">
                            <div className="flex items-center gap-3 mb-4">
                              <Package className="h-5 w-5 text-primary" />
                              <h3 className="font-semibold">Order Items</h3>
                            </div>
                            <div className="space-y-4">
                              {selectedOrder.items.map((item, idx) => (
                                <div key={idx} className="flex flex-col gap-3 p-4 bg-white/5 rounded-2xl border border-white/5">
                                  <div className="flex gap-4 items-center">
                                    <div className="h-16 w-16 rounded-xl bg-white/5 flex-shrink-0 overflow-hidden border border-white/10 cursor-zoom-in">
                                      {item.product_image ? (
                                        <img 
                                          src={item.product_image} 
                                          alt={item.product_name} 
                                          className="h-full w-full object-cover hover:scale-110 transition-transform duration-500" 
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setImageModal({
                                              isOpen: true,
                                              imageUrl: item.product_image,
                                              productName: item.product_name || "Product",
                                            });
                                          }}
                                        />
                                      ) : (
                                        <div className="h-full w-full flex items-center justify-center">
                                          <ShoppingBag className="h-6 w-6 text-muted-foreground/40" />
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-sm truncate">{item.product_name || 'Loading...'}</p>
                                      <p className="text-xs text-muted-foreground mt-0.5">
                                        {item.color && `Colour: ${item.color} • `}
                                        Quantity: {item.quantity}
                                      </p>
                                    </div>
                                    <p className="font-semibold text-sm">₹{(item.product_price * item.quantity).toFixed(2)}</p>
                                  </div>
                                  
                                  {item.note && (
                                    <div className="bg-amber-500/10 p-3 rounded-xl border border-amber-500/20">
                                      <p className="text-[10px] text-amber-500 uppercase tracking-wider font-bold mb-1">⚠️ Customer Instruction / Note</p>
                                      <p className="text-sm italic text-foreground leading-relaxed font-medium">"{item.note}"</p>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                            <div className="border-t border-white/10 mt-6 pt-6 flex justify-between items-center">
                              <p className="font-semibold">Total Amount</p>
                              <p className="text-2xl font-bold text-primary">₹{selectedOrder.total_amount.toFixed(2)}</p>
                            </div>
                          </div>

                      {(selectedOrder.order_status === 'accepted' || selectedOrder.order_status === 'confirmed') && (
                        <div className="glass-morphic rounded-2xl p-6 border-white/5 space-y-4">
                          <div className="flex items-center gap-3">
                            <Calendar className="h-5 w-5 text-primary" />
                            <h3 className="font-semibold">Delivery Timeframe</h3>
                          </div>
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Estimated Timeframe</Label>
                              <Input
                                value={trackingInfo[selectedOrder.id]?.delivery_timeframe || ''}
                                onChange={(e) => updateTrackingInfo(selectedOrder.id, 'delivery_timeframe', e.target.value)}
                                placeholder="e.g. 3-5 business days"
                                className="bg-white/5 border-white/10"
                              />
                            </div>
                            <Button
                              onClick={() => saveTrackingInfo(selectedOrder.id)}
                              className="w-full rounded-full bg-primary hover:bg-primary/90"
                            >
                              Update Timeframe & Notify
                            </Button>
                          </div>
                        </div>
                      )}

                      {(selectedOrder.order_status === 'shipped' || selectedOrder.order_status === 'delivered') && (
                        <div className="glass-morphic rounded-2xl p-6 border-white/5 space-y-4">
                          <div className="flex items-center gap-3">
                            <Truck className="h-5 w-5 text-primary" />
                            <h3 className="font-semibold">Tracking Information</h3>
                          </div>
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Courier Service</Label>
                              <Select 
                                value={trackingInfo[selectedOrder.id]?.courier_service || ''} 
                                onValueChange={(value) => updateTrackingInfo(selectedOrder.id, 'courier_service', value)}
                              >
                                <SelectTrigger className="bg-white/5 border-white/10">
                                  <SelectValue placeholder="Select courier" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="bluedart">Blue Dart</SelectItem>
                                  <SelectItem value="delhivery">Delhivery</SelectItem>
                                  <SelectItem value="dtdc">DTDC</SelectItem>
                                  <SelectItem value="ecom">Ecom Express</SelectItem>
                                  <SelectItem value="fedex">FedEx</SelectItem>
                                  <SelectItem value="indiapost">India Post</SelectItem>
                                  <SelectItem value="professional">Professional Couriers</SelectItem>
                                  <SelectItem value="shadowfax">Shadowfax</SelectItem>
                                  <SelectItem value="xpressbees">Xpressbees</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tracking Number</Label>
                              <Input
                                value={trackingInfo[selectedOrder.id]?.tracking_number || ''}
                                onChange={(e) => updateTrackingInfo(selectedOrder.id, 'tracking_number', e.target.value)}
                                placeholder="Enter tracking number"
                                className="bg-white/5 border-white/10"
                              />
                            </div>
                            <Button
                              onClick={() => saveTrackingInfo(selectedOrder.id)}
                              className="w-full rounded-full bg-primary hover:bg-primary/90"
                            >
                              Update Tracking
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <ImageModal
          isOpen={imageModal.isOpen}
          onClose={() => setImageModal({ ...imageModal, isOpen: false })}
          imageUrl={imageModal.imageUrl}
          productName={imageModal.productName}
        />
      </div>
    </div>
  );
}
